import { supabase } from '../lib/supabaseClient';
import { ApiErrorConCodigo, esErrorPermanente } from '../lib/apiErrors';
import { setVisitaUploader, type VisitaEnCola } from '../offline/visitaQueue';
import { setJornadaUploader, type JornadaEnCola } from '../offline/jornadaQueue';
// Las dos claves fijas se IMPORTAN en vez de escribirse a mano acá: son el mismo literal con el
// que `fotosDelBorrador` etiqueta las fotos, y dos copias del literal se desincronizan calladas —
// la foto se subiría con una clave que este payload nunca busca, y la visita se registraría sin
// ella aunque el archivo estuviera en el bucket.
import {
  CLAVE_FOTO_HALLAZGO, CLAVE_FOTO_PUESTO, COLUMNA_DE_SECCION, SECCIONES_OBSERVABLES,
  TITULO_DE_SECCION, campaniasParaElegir, categoriasDesdeServidor, claveDeMercado,
  precioUnitario, unidadesEfectivas,
  type CampaniaElegible, type CategoriaProducto, type EstadoVisita, type MarcaLevantamiento,
  type PrecioPor, type PresentacionMarca, type SeccionObservable,
} from '../domain/levantamiento';

const BUCKET = 'photos';

/** Error del levantamiento con su código, para que la cola distinga red de rechazo. */
export class LevantamientoApiError extends ApiErrorConCodigo {
  constructor(message: string, code: string | null = null) {
    super(message, code, 'LevantamientoApiError');
  }
}

const aNumero = (v: string | number | null): number | null => (v === null ? null : Number(v));

// ---------------------------------------------------------------------------
// Configuración de la campaña
// ---------------------------------------------------------------------------

type MarcaRaw = {
  id: string; nombre: string; categoria: string; es_propia: boolean;
  es_referencia_barata: boolean;
  pide_precio: boolean; pide_unidades: boolean;
  precio_min: string | number | null; precio_max: string | number | null; sort: number;
  ppu_min: string | number | null; ppu_max: string | number | null;
};

/**
 * Las marcas de la cuadrícula, en orden. Salen de la base y no de una lista en el código: son
 * las FILAS de la pregunta 6 y cambian con cada cliente (lección del §3.22 de la bitácora).
 *
 * `categorias` filtra EN EL SERVIDOR y es opcional. Las dos cosas importan:
 *
 * - Opcional, porque el celular pide las marcas de la campaña UNA sola vez y después filtra en
 *   memoria con `marcasDeCategorias` cada vez que la auditora cambia lo que el puesto vende. Ese
 *   filtro tiene que funcionar sin señal —es el caso normal dentro de un mercado— y un viaje al
 *   servidor por cada tilde no funciona ahí. Además deja intacta la llamada de una sola pantalla
 *   que ya existe.
 * - Y existe igual, porque el panel y los reportes sí quieren pedir «las marcas de pañales de esta
 *   campaña» sin traerse las demás para descartarlas.
 *
 * Una lista VACÍA se trata como «sin filtro» y no como «ninguna categoría»: es lo que devuelve un
 * `useState([])` recién montado, y responder con cero marcas ahí pintaría una cuadrícula vacía que
 * se lee como «esta campaña no mide nada».
 */
export async function listMarcasLevantamiento(
  campaignId: string, categorias: readonly CategoriaProducto[] = [],
): Promise<MarcaLevantamiento[]> {
  let q = supabase
    .from('levantamiento_marcas')
    // Una sola cadena literal y no dos concatenadas: `'a' + 'b'` pierde el tipo literal, y
    // supabase-js necesita el literal para tipar `data`. Con la concatenación, `data` sale como
    // `GenericStringError[]` y el mapeo de abajo deja de compilar.
    .select(`id, nombre, categoria, es_propia, es_referencia_barata,
             pide_precio, pide_unidades, precio_min, precio_max, ppu_min, ppu_max, sort`)
    .eq('campaign_id', campaignId)
    .eq('activa', true);
  if (categorias.length > 0) q = q.in('categoria', categorias as string[]);

  const { data, error } = await q.order('sort');
  if (error) throw new LevantamientoApiError(error.message, error.code ?? null);
  return ((data ?? []) as MarcaRaw[]).map((m) => ({
    id: m.id,
    nombre: m.nombre,
    // La columna es NOT NULL con CHECK desde la 0060, así que este respaldo no debería usarse
    // nunca. Está para que una categoría nueva en la base —agregada sin tocar el dominio— no
    // aparezca como `undefined` en un `Record` y haga desaparecer la marca de la cuadrícula sin
    // que nada falle; con el respaldo se ve en la sección de papel, que es visible y corregible.
    categoria: (categoriasDesdeServidor([m.categoria])[0] ?? 'papel_higienico'),
    esPropia: m.es_propia === true,
    esReferenciaBarata: m.es_referencia_barata,
    pidePrecio: m.pide_precio,
    pideUnidades: m.pide_unidades,
    precioMin: aNumero(m.precio_min),
    ppuMin: aNumero(m.ppu_min),
    ppuMax: aNumero(m.ppu_max),
    precioMax: aNumero(m.precio_max),
    sort: m.sort,
  }));
}

/**
 * Las presentaciones del catálogo de esas marcas, en el orden en que se muestran.
 *
 * Se pide POR MARCAS y no por campaña porque la tabla cuelga de la marca y no de la campaña: pedir
 * «las de esta campaña» obligaría a un join que PostgREST resuelve como embed anidado, para traer
 * exactamente lo mismo. La ficha ya tiene las marcas cargadas cuando necesita esto.
 *
 * Con la lista vacía DEVUELVE `[]` SIN CONSULTAR. No es un atajo: `.in('marca_id', [])` genera un
 * `in.()` que PostgREST rechaza con un error de sintaxis, y ese error aparecería justo en el primer
 * render, cuando las marcas todavía no cargaron.
 */
export async function listPresentacionesDeMarcas(
  marcaIds: readonly string[],
): Promise<PresentacionMarca[]> {
  if (marcaIds.length === 0) return [];
  const { data, error } = await supabase
    .from('levantamiento_presentaciones')
    .select('id, marca_id, nombre, unidades, orden')
    .in('marca_id', marcaIds as string[])
    .eq('activa', true)
    .order('marca_id')
    .order('orden')
    .order('nombre');
  if (error) throw new LevantamientoApiError(error.message, error.code ?? null);
  type Raw = {
    id: string; marca_id: string; nombre: string; unidades: number | null; orden: number;
  };
  return ((data ?? []) as Raw[]).map((p) => ({
    id: p.id,
    marcaId: p.marca_id,
    nombre: p.nombre,
    unidades: p.unidades,
    orden: p.orden,
  }));
}

/** La forma la define el dominio: quién ve el banco de pruebas es una regla, no un detalle de API. */
export type CampaniaLevantamiento = CampaniaElegible;
export interface MercadoLevantamiento { id: string; code: string; nombre: string }

/**
 * Las campañas de levantamiento que se le pueden ofrecer a esta persona.
 *
 * ⚠️ **`roles` NO es opcional, y es lo que impide que se vuelva a colar el banco de pruebas.** Esta
 * consulta filtraba por tipo, estado y archivado, pero no por `es_banco_pruebas` —la exclusión sí
 * existía en Planificación y en el tablero de Hoy desde la 0041—, y el 19-ago un auditor eligió la
 * campaña demo del desplegable y registró ahí 18 puestos de trabajo real. Con los roles como
 * parámetro obligatorio, cualquier pantalla nueva que llame a esto tiene que decir para quién
 * pregunta: no se puede olvidar en silencio, que es como se olvidó la primera vez.
 *
 * El filtro va EN MEMORIA y no en la consulta a propósito: al de oficina el banco sí se le muestra,
 * rotulado. Ver `campaniasParaElegir`.
 */
export async function listCampaniasLevantamiento(
  roles: readonly string[],
): Promise<CampaniaLevantamiento[]> {
  const { data, error } = await supabase
    .from('campaigns')
    .select('id, name, client, brand, es_banco_pruebas')
    .eq('type', 'levantamiento')
    .eq('status', 'activa')
    .is('archived_at', null)
    .order('start_date', { ascending: false });
  if (error) throw new LevantamientoApiError(error.message, error.code ?? null);
  type Raw = {
    id: string; name: string | null; client: string; brand: string;
    es_banco_pruebas: boolean | null;
  };
  return campaniasParaElegir(
    ((data ?? []) as Raw[]).map((c) => ({
      id: c.id,
      nombre: c.name ?? `${c.client} — ${c.brand}`,
      // `=== true` y no un `??`: si la columna faltara en la respuesta, `undefined` tiene que
      // leerse como "no es banco de pruebas" y no colarse como verdadero por ser distinto de false.
      esBancoPruebas: c.es_banco_pruebas === true,
    })),
    roles,
  );
}

/**
 * Los mercados que ofrece la pregunta 1. El auditor ELIGE el mercado al llegar: no sale de una
 * asignación, así que el levantamiento no necesita programación previa (ver 0053).
 */
export async function listMercadosLevantamiento(
  campaignId: string,
): Promise<MercadoLevantamiento[]> {
  const { data, error } = await supabase
    .from('levantamiento_mercados')
    .select('sort, pdv:pdv_id(id, code, name)')
    .eq('campaign_id', campaignId)
    .eq('activo', true)
    .order('sort');
  if (error) throw new LevantamientoApiError(error.message, error.code ?? null);
  const filas = (data ?? []) as unknown as
    { pdv: { id: string; code: string; name: string } | null }[];
  return filas.filter((f) => f.pdv !== null)
    .map((f) => ({ id: f.pdv!.id, code: f.pdv!.code, nombre: f.pdv!.name }));
}

/**
 * Los nombres de la pregunta 2, «¿Quién eres?».
 *
 * Es lista y nunca texto libre: el indicador se cuenta por persona, y con texto libre «Vania» y
 * «vania puelles» son dos auditoras distintas. Hace falta mientras el sprint corra con cuentas
 * genéricas compartidas, donde la sesión no dice quién midió.
 */
export async function listAuditoresLevantamiento(campaignId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('levantamiento_auditores')
    .select('nombre, sort')
    .eq('campaign_id', campaignId)
    .eq('activo', true)
    .order('sort');
  if (error) throw new LevantamientoApiError(error.message, error.code ?? null);
  return ((data ?? []) as { nombre: string }[]).map((a) => a.nombre);
}

// ---------------------------------------------------------------------------
// Escritura
// ---------------------------------------------------------------------------

/**
 * Sube UNA foto de la visita y devuelve su ruta. `clave` dice a qué CAMPO pertenece (puesto,
 * hallazgo, precio-… o promo-…) e `indice` es su posición dentro de ese campo.
 *
 * ⚠️ EL ÍNDICE ES LA POSICIÓN DE LA FOTO, Y NO PUEDE SER OTRA COSA. Hay dos exigencias que tiran
 * para lados opuestos y solo la posición cumple las dos:
 *
 * - **Sin índice se perdían fotos en silencio.** Antes la ruta era `…-${clave}.jpg`, así que dos
 *   fotos del mismo campo generaban EL MISMO path y `upsert: true` hacía que la segunda pisara a
 *   la primera **sin devolver error**. La ficha quedaba declarando tres fotos del puesto y en el
 *   bucket había una sola, la última.
 * - **Con un aleatorio se llenaría el bucket de huérfanas.** Si esto fuera un `randomUUID()` o un
 *   contador de intentos, cada reintento de la cola sin señal —que reintenta LA MISMA foto, y
 *   dentro de un mercado reintenta muchas veces— estrenaría ruta. La visita quedaría registrada
 *   con las rutas del último intento y las de los anteriores seguirían ocupando espacio sin que
 *   nada las apunte.
 *
 * La posición dentro del campo distingue una foto de otra y NO cambia entre reintentos, porque
 * las fotos viajan en la cola en el orden en que se tomaron. Por eso `upsert: true` sigue siendo
 * lo correcto: reintentar reescribe el mismo objeto con el mismo contenido.
 *
 * El prefijo `form-photos/` no se toca. Desde la 0058 el servidor RECHAZA cualquier otra ruta:
 * el bucket `photos` guarda también las fotos de DNI bajo `documents/`, y estas columnas las abre
 * el panel con `getPhotoUrl`.
 */
export async function uploadVisitaPhoto(
  clientUuid: string, clave: string, indice: number, photo: Blob,
): Promise<string> {
  const path = `form-photos/visita-${clientUuid}-${clave}-${indice}.jpg`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, photo, {
    contentType: 'image/jpeg',
    upsert: true, // reintento de la cola: mismo path, mismo contenido
  });
  if (error) {
    const code = (error as { statusCode?: string | number }).statusCode;
    throw new LevantamientoApiError(error.message, code === undefined ? null : String(code));
  }
  return path;
}

/**
 * Registra la visita completa con UNA llamada: cabecera, cuadrícula, precios, promociones y
 * hallazgos entran en la misma transacción o no entra nada. Una ficha a medias es peor que
 * ninguna, porque parece un puesto medido y no lo está.
 *
 * `worker_id` NO viaja en el cuerpo: lo pone el servidor desde `auth.uid()`.
 *
 * `rutas` trae, por CLAVE DE CAMPO, las rutas ya subidas de todas sus fotos en el orden en que se
 * tomaron. La RPC es idempotente por `client_uuid`: reintentar devuelve el mismo id.
 */
export async function registrarVisita(
  input: VisitaEnCola, rutas: Record<string, string[]>,
): Promise<string> {
  /**
   * Las rutas de un campo. Lista VACÍA cuando no hay fotos, nunca `null`: desde la 0058 las
   * cuatro columnas son `text[] not null default '{}'`, y así el payload tiene una sola forma
   * —siempre un arreglo— en vez de dos que hay que distinguir al leer.
   */
  const rutasDe = (clave: string | null): string[] =>
    (clave === null ? [] : rutas[clave] ?? []);

  const { data, error } = await supabase.rpc('registrar_visita', {
    p_payload: {
      client_uuid: input.clientUuid,
      campaign_id: input.campaignId,
      pdv_id: input.pdvId,
      mercado_libre: input.mercadoLibre,
      assignment_id: input.assignmentId,
      fecha: input.fecha,
      numero_recorrido: input.numeroRecorrido,
      // Referencia libre para reconocer el puesto («el de doña Rosa»). OPCIONAL, y por eso puede
      // ir vacía: el servidor la guarda como `null` (0058). Sin ella la lista de avance no puede
      // distinguir dos puestos con el mismo número de uno cargado dos veces.
      nombre_puesto: input.nombrePuesto,
      auditor_declarado: input.auditorDeclarado,
      estado: input.estado,
      motivo_no_pude: input.motivoNoPude,
      giro: input.giro,
      // QUÉ VENDE el puesto. Viaja SIEMPRE como arreglo —vacío cuando no declaró ninguna— y nunca
      // como `undefined`: una clave con `undefined` se cae al serializar el JSON y el servidor
      // guardaría `'{}'` sin distinguir «no declaró» de «esta versión no lo manda».
      //
      // ⚠️ `?? []` y no `input.categorias` a secas: la cola sin señal guarda las fichas en
      // IndexedDB, así que una ficha encolada ANTES de este cambio se vuelve a leer sin esta clave.
      // Sin el respaldo llegaría `undefined` y pasaría lo de arriba, en el peor momento posible —
      // una ficha vieja que por fin encontró señal.
      categorias: input.categorias ?? [],
      cliente_principal: input.clientePrincipal,
      // Dato personal de un tercero (0060): el servidor lo guarda en dos columnas cuya lectura está
      // revocada a `authenticated`. Se manda igual que cualquier otro campo — lo que cambia es
      // quién puede leerlo después, no cómo entra.
      contacto_nombre: input.contactoNombre ?? null,
      contacto_telefono: input.contactoTelefono ?? null,
      hay_promocion: input.hayPromocion,
      marca_recomendada: input.marcaRecomendada,
      motivo_recomendacion: input.motivoRecomendacion,
      recomendacion_declinada: input.recomendacionDeclinada,
      vio_marca_desconocida: input.vioMarcaDesconocida,
      foto_puesto_paths: rutasDe(CLAVE_FOTO_PUESTO),
      lat: input.lat,
      lng: input.lng,
      accuracy: input.accuracy,
      origin: 'online',
      mediciones: input.mediciones.map((m) => ({
        marca_id: m.marcaId, presencia: m.presencia,
      })),
      precios: input.precios.map((p) => ({
        marca_id: p.marcaId,
        marca_declarada: p.marcaDeclarada,
        presentacion: p.presentacion,
        // Los dos con respaldo por la misma razón que `categorias`: una ficha encolada antes de
        // este cambio se relee de IndexedDB sin estas claves. `precio_por` en `null` es la forma en
        // que el servidor lee «paquete», o sea exactamente lo que esa ficha vieja quiso decir.
        presentacion_id: p.presentacionId ?? null,
        precio_por: p.precioPor ?? null,
        precio_paquete: p.precioPaquete,
        unidades_paquete: p.unidadesPaquete,
        foto_paths: rutasDe(p.fotoClave),
      })),
      promociones: input.promociones.map((p) => ({
        marca_id: p.marcaId,
        marca_libre: p.marcaLibre,
        de_quien: p.deQuien,
        descripcion: p.descripcion,
        foto_paths: rutasDe(p.fotoClave),
      })),
      // El hallazgo comparte una única clave fija porque la ficha registra UNA marca desconocida
      // por visita. Si algún día se pudieran anotar varias, cada una necesitaría su propia clave:
      // con la clave compartida las tres filas se llevarían las mismas fotos.
      hallazgos: input.hallazgos.map((h) => ({
        nombre_leido: h.nombreLeido,
        foto_empaque_paths: rutasDe(CLAVE_FOTO_HALLAZGO),
      })),
      ...Object.fromEntries(
        Object.entries(input.observaciones).map(
          ([k, v]) => [COLUMNA_DE_SECCION[k as SeccionObservable], v],
        ),
      ),
    },
  });
  if (error) throw new LevantamientoApiError(error.message, error.code ?? null);
  return data as string;
}

/** Cierre de jornada. Volver a mandarlo REEMPLAZA el del día: no se acumulan contradicciones. */
export async function registrarJornadaLevantamiento(input: JornadaEnCola): Promise<string> {
  const { data, error } = await supabase.rpc('registrar_jornada_levantamiento', {
    p_payload: {
      client_uuid: input.clientUuid,
      campaign_id: input.campaignId,
      pdv_id: input.pdvId,
      mercado_libre: input.mercadoLibre,
      fecha: input.fecha,
      afluencia: input.afluencia,
      actividad_adicional: input.actividadAdicional,
      factor_externo: input.factorExterno,
      competencia_activa: input.competenciaActiva,
      observacion: input.observacion,
    },
  });
  if (error) throw new LevantamientoApiError(error.message, error.code ?? null);
  return data as string;
}

// ---------------------------------------------------------------------------
// Lectura de lo PROPIO, para el celular del auditor
// ---------------------------------------------------------------------------

/**
 * Una visita propia, con lo mínimo para reconocerla en una lista.
 *
 * No trae el cuestionario: ni la lista de avance del mercado ni «Mi historial» muestran las
 * respuestas, y arrastrar las cuatro tablas hijas en cada relectura —que ocurre después de cada
 * puesto guardado— sería pagar la ficha entera para pintar una línea de texto.
 */
export interface VisitaPropiaRow {
  /** El mismo `client_uuid` que la cola local. Es lo que permite unir las dos fuentes sin duplicar. */
  clientUuid: string;
  id: string;
  campaignId: string;
  fecha: string;
  /** Hora del SERVIDOR en que aceptó la ficha (ISO). Es lo único comparable entre celulares. */
  registradoEn: string;
  /** `claveDeMercado` ya resuelta: el `pdv_id`, o `libre:<mercado escrito a mano>`. */
  claveMercado: string;
  /** Para mostrar: el nombre del PDV, o el texto que el auditor escribió a mano. */
  mercadoNombre: string;
  numeroRecorrido: number;
  nombrePuesto: string | null;
  estado: EstadoVisita;
}

export interface FiltrosMisVisitas {
  /** `null` = todas las campañas donde esta cuenta haya registrado (el historial). */
  campaignId: string | null;
  desde: string;
  /** `null` = sin tope superior. */
  hasta: string | null;
  /**
   * `null` = todos los mercados. Si viene, se compara contra `claveDeMercado`, que es la MISMA
   * función con la que agrupa la cola local: así las dos fuentes no pueden discrepar sobre qué
   * mercado es cuál.
   */
  claveMercado: string | null;
}

/**
 * Cuántas visitas propias se traen como máximo. Sesenta días de un auditor a treinta puestos
 * diarios pasan de mil, y PostgREST **trunca en silencio** en 1000: sin un tope explícito la lista
 * se cortaría sola sin decirlo. Con el tope el corte es una decisión declarada —las más recientes—
 * y no una casualidad del backend.
 */
const MIS_VISITAS_MAX = 500;

type RawVisitaPropia = {
  id: string; client_uuid: string; campaign_id: string; fecha: string; registrado_en: string;
  numero_recorrido: number; nombre_puesto: string | null; estado: string;
  pdv_id: string | null; mercado_libre: string | null;
  pdv: { id: string; name: string } | null;
};

/**
 * Las visitas que registró ESTA CUENTA, para las pantallas del celular.
 *
 * POR QUÉ EXISTE (2026-08-19). La lista de avance del mercado se armaba SOLO con la cola local del
 * teléfono, y eso se rompió el primer día de uso real: el auditor entró desde otra máquina, donde
 * la base local está vacía, y la pantalla le dijo «todavía no registraste ningún puesto» con 22
 * visitas suyas en el servidor. Y aun en el mismo celular la cola no es el histórico: lo ya subido
 * se purga a los pocos días y lo registrado antes de que la cola guardara los enviados nunca
 * estuvo ahí. El servidor es la fuente de verdad; el celular solo aporta lo que todavía no subió.
 *
 * ⚠️ **`worker_id = auth.uid()` SE FILTRA ACÁ Y NO SE PUEDE QUITAR.** La política de lectura de
 * `levantamiento_visitas` es `worker_id = auth.uid() OR has_role('admin') OR
 * can_view_campaign(campaign_id)`: para un auditor la RLS ya devuelve solo lo suyo, pero para un
 * coordinador o un admin devolvería la campaña ENTERA. Sin este filtro, la pantalla que dice «lo
 * que registró esta cuenta» le mostraría a un coordinador los puestos de todo el equipo, y su
 * conteo de avance —y la bandera de número repetido, que sale de la misma lista— pasarían a hablar
 * de un recorrido que no es el suyo.
 *
 * EL MERCADO SE FILTRA EN MEMORIA a propósito. Puede venir de dos columnas distintas (`pdv_id`
 * cuando salió de la lista, `mercado_libre` cuando se escribió a mano) y la clave del texto libre
 * está recortada y en minúsculas, así que en el servidor haría falta un `ilike` cuyo patrón se
 * rompería con un `%` o un `_` en el nombre del mercado. Un día de trabajo son decenas de filas:
 * se traen las del rango y se comparan con la misma función que usa la cola local.
 */
export async function listMisVisitas(f: FiltrosMisVisitas): Promise<VisitaPropiaRow[]> {
  const { data: sesion } = await supabase.auth.getUser();
  const uid = sesion?.user?.id;
  // Sin sesión no hay «lo mío». Devolver vacío y no lanzar: la pantalla que llama a esto sigue
  // funcionando sin la lista, y en el celular «todavía no cargó la sesión» es un instante normal.
  if (!uid) return [];

  let q = supabase
    .from('levantamiento_visitas')
    .select(`id, client_uuid, campaign_id, fecha, registrado_en, numero_recorrido,
             nombre_puesto, estado, pdv_id, mercado_libre, pdv:pdv_id(id, name)`)
    .eq('worker_id', uid)
    .gte('fecha', f.desde);
  if (f.hasta !== null) q = q.lte('fecha', f.hasta);
  if (f.campaignId !== null) q = q.eq('campaign_id', f.campaignId);

  const { data, error } = await q
    .order('registrado_en', { ascending: false })
    .limit(MIS_VISITAS_MAX);
  // Acá SÍ se lanza, al revés que con la sesión ausente: quien llama tiene que poder distinguir
  // «no registraste nada» de «no se pudo preguntar», que es justamente el aviso que la lista de
  // avance muestra cuando no hay señal. Devolver `[]` en un fallo pintaría un cero mentiroso.
  if (error) throw new LevantamientoApiError(error.message, error.code ?? null);

  return ((data ?? []) as unknown as RawVisitaPropia[]).map((r) => ({
    clientUuid: r.client_uuid,
    id: r.id,
    campaignId: r.campaign_id,
    fecha: r.fecha,
    registradoEn: r.registrado_en,
    claveMercado: claveDeMercado(r.pdv_id ?? '', r.mercado_libre ?? ''),
    // El PDV manda sobre el texto libre porque cuando hay `pdv_id` el `mercado_libre` no se usa.
    // El respaldo cubre el caso que la base no debería permitir (ninguno de los dos) para que la
    // pantalla no termine pintando «undefined» en la línea del historial.
    mercadoNombre: r.pdv?.name ?? ((r.mercado_libre ?? '').trim() || 'Mercado sin nombre'),
    numeroRecorrido: r.numero_recorrido,
    // Se recorta igual que en la cola local (`visitasLocalesDe`): si una fuente dijera
    // «Doña Rosa » y la otra «Doña Rosa», el mismo puesto se leería como dos textos distintos.
    nombrePuesto: (r.nombre_puesto ?? '').trim() === '' ? null : r.nombre_puesto!.trim(),
    estado: r.estado as EstadoVisita,
  })).filter((v) => f.claveMercado === null || v.claveMercado === f.claveMercado);
}

// ---------------------------------------------------------------------------
// Lectura para la bandeja del panel
// ---------------------------------------------------------------------------

export interface VisitaMedicionRow { marcaNombre: string; presencia: string }

/**
 * Las fotos de cada fila son una LISTA, y «no hay fotos» es la lista vacía — nunca `null`.
 *
 * Antes era `fotoPath: string | null` y el panel tenía que escribir `p.fotoPath && … p.fotoPath!`
 * en cada uno de los cuatro sitios donde las pinta: el `!` es una promesa a mano de que ese valor
 * no es nulo, y una promesa a mano se rompe la primera vez que alguien mueve la condición. Con
 * una lista, «cero fotos» y «tres fotos» se pintan con el mismo `map` y no hay nada que prometer.
 */
export interface VisitaPrecioRow {
  marcaNombre: string;
  /**
   * El rango de precio por unidad DE SU MARCA (0062). Viaja con la fila porque el panel tiene que
   * marcar «sospechoso» con el MISMO criterio que el servidor usó para poner la bandera; si acá se
   * usara otra banda, la pantalla y la bandera dirían cosas distintas del mismo precio.
   *
   * `null` en cualquiera de los dos lados = ese lado no se evalúa (marca sin calibrar).
   */
  ppuMin: number | null;
  ppuMax: number | null;
  /**
   * Cómo se muestra la presentación: el nombre del CATÁLOGO si eligió uno, y si no el texto que
   * tecleó. Se resuelve acá y no en la pantalla porque son dos columnas que dicen lo mismo y
   * resolverlas en cada sitio donde se pintan es cómo terminan diciendo cosas distintas.
   */
  presentacion: string | null;
  precioPaquete: number;
  /** `null` en las 37 filas anteriores a la 0060; se lee como `'paquete'`. */
  precioPor: PrecioPor | null;
  /** Lo que tecleó la auditora. `null` = no lo tecleó (puede venir de la presentación). */
  unidadesPaquete: number | null;
  /** Las del catálogo, cuando eligió una presentación y esa presentación las declara. */
  unidadesPresentacion: number | null;
  /**
   * El precio por unidad YA CALCULADO, o `null` cuando no se puede calcular.
   *
   * Viene resuelto desde acá y no desde la pantalla porque la cuenta depende de tres columnas y de
   * una regla —qué significa `precio_por = null`, y que lo tecleado manda sobre el catálogo— que
   * ya está escrita una sola vez en el dominio. Cada pantalla que la repita va a repetirla distinto,
   * y el panel ya tenía su propia llamada a `precioPorUnidad` con dos argumentos que ignoraba la
   * presentación.
   */
  precioUnitario: number | null;
  fotoPaths: string[];
}
export interface VisitaPromocionRow {
  marcaNombre: string; deQuien: string; descripcion: string; fotoPaths: string[];
}
export interface VisitaHallazgoRow {
  id: string; nombreLeido: string; estado: string; fotoPaths: string[];
}

export interface VisitaRow {
  id: string;
  campaignId: string;
  fecha: string;
  registradoEn: string;
  numeroRecorrido: number;
  /**
   * Referencia libre del puesto, opcional (0058). Es lo único que distingue dos puestos con el
   * mismo `numeroRecorrido` —que se repite: es el número real escrito en el mercado— de una misma
   * visita cargada dos veces.
   */
  nombrePuesto: string | null;
  auditorDeclarado: string | null;
  estado: 'medido' | 'no_pude' | 'anulado';
  motivoNoPude: string | null;
  giro: string | null;
  /** QUÉ VENDE el puesto (0060). Vacío = una visita anterior a la migración sin `giro`, o una
   * `no_pude`, o un puesto de abarrotes que no declaró ninguna. */
  categorias: CategoriaProducto[];
  clientePrincipal: string | null;
  /**
   * El contacto del puestero, o `null`.
   *
   * ⚠️ `null` ACÁ SIGNIFICA DOS COSAS DISTINTAS y la pantalla no puede distinguirlas: que la visita
   * no trae contacto, o que **no se pidió** (`incluirContacto: false`, que es el valor por omisión).
   * Es a propósito: el tipo no cambia según el filtro, así que ninguna pantalla puede quedar
   * pintando `undefined`. Lo que la pantalla sí sabe es qué pidió.
   */
  contacto: { nombre: string | null; telefono: string | null } | null;
  hayPromocion: boolean | null;
  marcaRecomendada: string | null;
  motivoRecomendacion: string | null;
  recomendacionDeclinada: boolean;
  vioMarcaDesconocida: boolean | null;
  fotoPuestoPaths: string[];
  lat: number | null;
  lng: number | null;
  flags: string[];
  anulacionMotivo: string | null;
  mercadoLibre: string | null;
  /**
   * Las observaciones CON TEXTO, en el orden del formulario.
   *
   * `campo` es el nombre de la columna (`obs_puesto`, …) y no adorno: es lo que permite que el
   * modal de corrección sepa qué columna corregir sin tener que adivinarla desde el título en
   * español. Sin él habría que mapear «Cuadrícula» → `obs_cuadricula` a mano en la pantalla, y ese
   * mapeo se rompe en silencio el día que alguien mejore un título.
   */
  observaciones: { campo: string; seccion: string; texto: string }[];
  pdv: { id: string; name: string } | null;
  worker: { id: string; dni: string; firstNames: string; lastNames: string } | null;
  mediciones: VisitaMedicionRow[];
  precios: VisitaPrecioRow[];
  promociones: VisitaPromocionRow[];
  hallazgos: VisitaHallazgoRow[];
}

export interface FiltrosBandejaVisitas {
  campaignId: string;
  desde: string;
  hasta: string;
  pdvId: string | null;
  workerId: string | null;
  estado: 'medido' | 'no_pude' | null;
  soloConBanderas: boolean;
  /**
   * Traer o no el contacto del puestero. **Opcional y `false` por omisión, a propósito.**
   *
   * Es dato personal de un tercero que no es usuario del sistema. Que la bandeja lo pida solo cuando
   * alguien lo pide de verdad significa que el número de teléfono no viaja por la red, ni queda en
   * la memoria del navegador, ni en un log intermedio, en el 99% de las veces que se abre la
   * pantalla — que es el uso normal, revisar mediciones.
   *
   * En `true` **cuesta una llamada más**: las dos columnas están revocadas a `authenticated` (0060)
   * y solo se pueden leer por `get_contactos_visitas`, que además comprueba el permiso y deja
   * rastro en `audit_log`. Si quien pregunta no es el jefe de operaciones ni el coordinador
   * designado de la campaña, las visitas vuelven con `contacto: null` y **sin error**: la bandeja
   * tiene que poder pintarse igual, y un 403 en medio de la lista la dejaría en blanco entera por
   * un dato que es accesorio.
   */
  incluirContacto?: boolean;
}

/** Igual que en las otras bandejas: PostgREST trunca en silencio a 1000 filas. */
const VISITA_PAGE_SIZE = 1000;

/**
 * Las columnas de foto son las PLURALES de la 0058. Las viejas en singular siguen existiendo en
 * la base, pero quedaron congeladas con lo que había antes de la migración: pedirlas acá pintaría
 * una sola foto —o ninguna, en todo lo registrado después— y se leería como si el auditor no
 * hubiera tomado las demás.
 */
/**
 * ⚠️ `contacto_nombre` y `contacto_telefono` NO ESTÁN ACÁ, Y NO ES UN OLVIDO. La 0060 les revocó el
 * SELECT a `authenticated`: pedirlas en esta consulta haría fallar la bandeja ENTERA con un 42501
 * —incluso para el jefe de operaciones—, no solo esas dos columnas. Se leen aparte, por
 * `get_contactos_visitas`, cuando el filtro lo pide.
 */
const VISITA_SELECT = `
  id, campaign_id, fecha, registrado_en, numero_recorrido, nombre_puesto, auditor_declarado,
  estado, motivo_no_pude, giro, categorias, cliente_principal, hay_promocion,
  marca_recomendada, motivo_recomendacion, recomendacion_declinada,
  vio_marca_desconocida, foto_puesto_paths, lat, lng, flags, anulacion_motivo, mercado_libre,
  obs_puesto, obs_giro, obs_cliente, obs_cuadricula, obs_precios, obs_promocion,
  obs_recomendacion, obs_evidencia,
  pdv:pdv_id(id, name),
  worker:profiles!levantamiento_visitas_worker_id_fkey(id, dni, first_names, last_names),
  levantamiento_mediciones(presencia, marca:levantamiento_marcas(nombre)),
  levantamiento_precios(precio_paquete, unidades_paquete, precio_por, marca_declarada, presentacion,
                        foto_paths, marca:levantamiento_marcas(nombre, ppu_min, ppu_max),
                        presentacion_catalogo:presentacion_id(nombre, unidades)),
  levantamiento_promociones(de_quien, descripcion, marca_libre, foto_paths,
                            marca:levantamiento_marcas(nombre)),
  levantamiento_hallazgos(id, nombre_leido, estado, foto_empaque_paths)`;

/**
 * La fila cruda. Los cuatro arreglos de foto se declaran `| null` aunque la columna sea
 * `not null default '{}'`: lo que llega es JSON de PostgREST, y si mañana alguien saca la columna
 * del select o la sirve por una vista, un `undefined` acá reventaría el `.map` del panel con un
 * «cannot read properties of null». Se normalizan a `[]` en un solo sitio, el mapeo de abajo.
 */
type RawVisita = {
  id: string; campaign_id: string; fecha: string; registrado_en: string;
  numero_recorrido: number; nombre_puesto: string | null; auditor_declarado: string | null;
  estado: string; motivo_no_pude: string | null;
  giro: string | null; categorias: string[] | null;
  cliente_principal: string | null; hay_promocion: boolean | null;
  marca_recomendada: string | null; motivo_recomendacion: string | null;
  recomendacion_declinada: boolean; vio_marca_desconocida: boolean | null;
  foto_puesto_paths: string[] | null; lat: number | null; lng: number | null;
  flags: string[] | null; anulacion_motivo: string | null; mercado_libre: string | null;
  obs_puesto: string | null; obs_giro: string | null; obs_cliente: string | null;
  obs_cuadricula: string | null; obs_precios: string | null; obs_promocion: string | null;
  obs_recomendacion: string | null; obs_evidencia: string | null;
  pdv: { id: string; name: string } | null;
  worker: { id: string; dni: string; first_names: string; last_names: string } | null;
  levantamiento_mediciones: { presencia: string; marca: { nombre: string } | null }[] | null;
  levantamiento_precios: {
    precio_paquete: string | number; unidades_paquete: number | null;
    precio_por: string | null;
    marca_declarada: string | null; presentacion: string | null; foto_paths: string[] | null;
    marca: { nombre: string; ppu_min: string | number | null; ppu_max: string | number | null } | null;
    presentacion_catalogo: { nombre: string; unidades: number | null } | null;
  }[] | null;
  levantamiento_promociones: {
    de_quien: string; descripcion: string; marca_libre: string | null;
    foto_paths: string[] | null; marca: { nombre: string } | null;
  }[] | null;
  levantamiento_hallazgos: {
    id: string; nombre_leido: string; estado: string; foto_empaque_paths: string[] | null;
  }[] | null;
};

/**
 * Las visitas del rango, con su desglose.
 *
 * Pagina y COMPRUEBA el total: una bandeja que devuelve 1000 de 1200 filas sin decirlo es peor
 * que una que falla, porque los números se leen como si fueran completos.
 */
export async function listVisitasBandeja(f: FiltrosBandejaVisitas): Promise<VisitaRow[]> {
  const filas: RawVisita[] = [];
  let total: number | null = null;
  for (let from = 0; ; from += VISITA_PAGE_SIZE) {
    let q = supabase
      .from('levantamiento_visitas')
      .select(VISITA_SELECT, { count: 'exact' })
      .eq('campaign_id', f.campaignId)
      .gte('fecha', f.desde)
      .lte('fecha', f.hasta);
    if (f.pdvId) q = q.eq('pdv_id', f.pdvId);
    if (f.workerId) q = q.eq('worker_id', f.workerId);
    if (f.estado) q = q.eq('estado', f.estado);
    const { data, error, count } = await q
      .order('fecha', { ascending: false })
      .order('numero_recorrido')
      .order('id')
      .range(from, from + VISITA_PAGE_SIZE - 1);
    if (error) throw new LevantamientoApiError(error.message, error.code ?? null);
    if (count !== null) total = count;
    const lote = (data ?? []) as unknown as RawVisita[];
    filas.push(...lote);
    if (lote.length < VISITA_PAGE_SIZE) break;
  }
  if (total !== null && filas.length !== total) {
    throw new LevantamientoApiError(
      `Lectura incompleta: se recibieron ${filas.length} de ${total} visitas. `
      + 'Reduce el rango de fechas y vuelve a intentar.',
    );
  }

  const salida: VisitaRow[] = filas.map((r) => ({
    id: r.id,
    campaignId: r.campaign_id,
    fecha: r.fecha,
    registradoEn: r.registrado_en,
    numeroRecorrido: r.numero_recorrido,
    nombrePuesto: r.nombre_puesto,
    auditorDeclarado: r.auditor_declarado,
    estado: r.estado as VisitaRow['estado'],
    motivoNoPude: r.motivo_no_pude,
    giro: r.giro,
    // Se filtran las que el dominio conoce en vez de castear el `string[]` que llega: un valor nuevo
    // en la base y no en el código pasaría igual con un `as`, y reventaría más adelante, lejos de
    // acá. Ver `categoriasDesdeServidor`.
    categorias: categoriasDesdeServidor(r.categorias),
    clientePrincipal: r.cliente_principal,
    // Se rellena abajo, y solo si el filtro lo pidió: las columnas no se pueden leer por PostgREST.
    contacto: null,
    hayPromocion: r.hay_promocion,
    marcaRecomendada: r.marca_recomendada,
    motivoRecomendacion: r.motivo_recomendacion,
    recomendacionDeclinada: r.recomendacion_declinada,
    vioMarcaDesconocida: r.vio_marca_desconocida,
    fotoPuestoPaths: r.foto_puesto_paths ?? [],
    lat: r.lat,
    lng: r.lng,
    flags: r.flags ?? [],
    anulacionMotivo: r.anulacion_motivo,
    mercadoLibre: r.mercado_libre,
    // La lista de secciones y sus nombres salen del dominio, no de ocho pares escritos acá: eran
    // la tercera copia de la misma tabla —la ficha y la bandeja tenían las suyas— y el día que se
    // agregue una novena sección, esta copia la dejaría fuera del reporte sin fallar.
    // El acceso por nombre de columna necesita el índice de cadena que `RawVisita` no declara; se
    // hace en UN solo sitio y a partir de un nombre que sale de `COLUMNA_DE_SECCION`, no del aire.
    observaciones: SECCIONES_OBSERVABLES.flatMap((k) => {
      const columna = COLUMNA_DE_SECCION[k];
      const texto = (r as unknown as Record<string, string | null>)[columna];
      if (texto === null || texto === undefined || texto.trim() === '') return [];
      return [{ campo: columna, seccion: TITULO_DE_SECCION[k], texto }];
    }),
    pdv: r.pdv,
    worker: r.worker
      ? {
        id: r.worker.id, dni: r.worker.dni,
        firstNames: r.worker.first_names, lastNames: r.worker.last_names,
      }
      : null,
    mediciones: (r.levantamiento_mediciones ?? []).map((m) => ({
      marcaNombre: m.marca?.nombre ?? '—', presencia: m.presencia,
    })),
    precios: (r.levantamiento_precios ?? []).map((p) => {
      const precio = Number(p.precio_paquete);
      // `precio_por` se valida contra las dos opciones en vez de castearse: la columna acepta NULL
      // (las 37 filas anteriores a la 0060) y cualquier otra cosa sería un valor que el CHECK no
      // debería haber dejado entrar. Lo que no se reconoce se lee como `null`, que el dominio
      // interpreta como «paquete» — que es lo que esas filas quisieron decir.
      const precioPor: PrecioPor | null =
        p.precio_por === 'unidad' || p.precio_por === 'paquete' ? p.precio_por : null;
      const unidadesPresentacion = p.presentacion_catalogo?.unidades ?? null;
      return {
        marcaNombre: p.marca_declarada ?? p.marca?.nombre ?? '—',
        ppuMin: aNumero(p.marca?.ppu_min ?? null),
        ppuMax: aNumero(p.marca?.ppu_max ?? null),
        // El catálogo manda sobre el texto tecleado para MOSTRAR, al revés que con las unidades:
        // acá lo que importa es que la misma presentación se lea siempre igual —en producción hay
        // nueve escrituras distintas de dos presentaciones—, y para eso el nombre canónico es mejor
        // que el que tecleó cada auditora.
        presentacion: p.presentacion_catalogo?.nombre ?? p.presentacion,
        precioPaquete: precio,
        precioPor,
        unidadesPaquete: p.unidades_paquete,
        unidadesPresentacion,
        precioUnitario: precioUnitario(
          precio, precioPor, unidadesEfectivas(p.unidades_paquete, unidadesPresentacion),
        ),
        fotoPaths: p.foto_paths ?? [],
      };
    }),
    promociones: (r.levantamiento_promociones ?? []).map((p) => ({
      marcaNombre: p.marca_libre ?? p.marca?.nombre ?? '—',
      deQuien: p.de_quien,
      descripcion: p.descripcion,
      fotoPaths: p.foto_paths ?? [],
    })),
    hallazgos: (r.levantamiento_hallazgos ?? []).map((h) => ({
      id: h.id, nombreLeido: h.nombre_leido, estado: h.estado,
      fotoPaths: h.foto_empaque_paths ?? [],
    })),
  }));

  const visibles = f.soloConBanderas ? salida.filter((v) => v.flags.length > 0) : salida;

  // El contacto se pide DESPUÉS y solo sobre lo que se va a mostrar: filtrar primero por banderas
  // evita pedir —y auditar— los teléfonos de visitas que la pantalla ni siquiera va a pintar.
  if (f.incluirContacto === true && visibles.length > 0) {
    const contactos = await getContactosVisitas(visibles.map((v) => v.id));
    for (const v of visibles) {
      const c = contactos.get(v.id);
      if (c !== undefined) v.contacto = c;
    }
  }

  return visibles;
}

/**
 * Los contactos de un lote de visitas, indexados por id de visita.
 *
 * Es la ÚNICA forma de leerlos: la 0060 revocó el SELECT de esas dos columnas a `authenticated`,
 * igual que la 0002 hizo con la cuenta bancaria de `profiles`, porque la RLS es row-level y una
 * política que deja ver la fila deja ver todas sus columnas — y la visita la ve cualquier
 * trabajador asignado a la campaña, no solo quien tendría que llamar al puestero.
 *
 * La RPC devuelve solo lo que quien pregunta puede ver (jefe de operaciones o coordinador designado
 * de la campaña) y deja una fila en `audit_log`. Para el resto devuelve un mapa vacío y **no
 * lanza**: no poder ver un teléfono no es un error, es el caso esperado para la mayoría, y hacerlo
 * fallar dejaría la bandeja en blanco por un dato accesorio.
 */
export async function getContactosVisitas(
  visitaIds: readonly string[],
): Promise<Map<string, { nombre: string | null; telefono: string | null }>> {
  const salida = new Map<string, { nombre: string | null; telefono: string | null }>();
  if (visitaIds.length === 0) return salida;

  const { data, error } = await supabase.rpc('get_contactos_visitas', {
    p_visitas: visitaIds as string[],
  });
  if (error) throw new LevantamientoApiError(error.message, error.code ?? null);

  type Raw = { visita_id: string; contacto_nombre: string | null; contacto_telefono: string | null };
  for (const r of (data ?? []) as Raw[]) {
    salida.set(r.visita_id, { nombre: r.contacto_nombre, telefono: r.contacto_telefono });
  }
  return salida;
}

/**
 * Corrige los campos de la lista blanca de una visita, dejando rastro (0059).
 *
 * `cambios` viaja como texto plano por columna: el servidor castea al tipo de la columna, rechaza
 * cualquier campo fuera de su lista blanca y traduce al español lo que rebote un CHECK. Acá no se
 * valida nada de eso a propósito — una segunda copia de la lista blanca en el cliente se
 * desincroniza, y la que se olvida de actualizar es siempre la que promete de más.
 *
 * Una cadena vacía BORRA el dato (queda NULL). `fecha` y `numero_recorrido` son NOT NULL y el
 * servidor las rechaza diciendo cuál.
 */
export async function corregirVisita(
  visitaId: string, cambios: Record<string, string>, motivo: string,
): Promise<void> {
  const { error } = await supabase.rpc('corregir_visita', {
    p_record: visitaId, p_cambios: cambios, p_motivo: motivo,
  });
  if (error) throw new LevantamientoApiError(error.message, error.code ?? null);
}

/**
 * Anula una visita. NO la borra: queda visible como anulada y sale de los conteos.
 *
 * Desde la 0059 la anulación también deja fila en `levantamiento_correcciones`, así que el
 * historial del registro incluye el cambio de estado y no solo las correcciones de campo.
 */
export async function anularVisita(visitaId: string, motivo: string): Promise<void> {
  const { error } = await supabase.rpc('anular_visita', {
    p_visita: visitaId, p_motivo: motivo,
  });
  if (error) throw new LevantamientoApiError(error.message, error.code ?? null);
}

export interface CorreccionVisitaRow {
  id: string;
  campo: string;
  valorAnterior: string | null;
  valorNuevo: string | null;
  motivo: string;
  createdAt: string;
  actor: { firstNames: string; lastNames: string } | null;
}

/**
 * El rastro de correcciones de una visita, de la más reciente a la más antigua.
 *
 * Es lo que hace legítimo corregir una medición de campo. La 0047 no permitía corregirlas
 * justamente porque «editarla la vuelve indistinguible de una inventada»; lo que vuelve a
 * distinguirlas es esto: queda el valor anterior, quién lo cambió y por qué.
 */
export async function listCorreccionesDeVisita(
  visitaId: string,
): Promise<CorreccionVisitaRow[]> {
  const { data, error } = await supabase
    .from('levantamiento_correcciones')
    .select(
      `id, campo, valor_anterior, valor_nuevo, motivo, created_at,
       actor:profiles!levantamiento_correcciones_actor_id_fkey(first_names, last_names)`,
    )
    .eq('record_id', visitaId)
    .order('created_at', { ascending: false });
  if (error) throw new LevantamientoApiError(error.message, error.code ?? null);
  type Raw = {
    id: string; campo: string; valor_anterior: string | null; valor_nuevo: string | null;
    motivo: string; created_at: string;
    actor: { first_names: string; last_names: string } | null;
  };
  return ((data ?? []) as unknown as Raw[]).map((r) => ({
    id: r.id,
    campo: r.campo,
    valorAnterior: r.valor_anterior,
    valorNuevo: r.valor_nuevo,
    motivo: r.motivo,
    createdAt: r.created_at,
    actor: r.actor
      ? { firstNames: r.actor.first_names, lastNames: r.actor.last_names }
      : null,
  }));
}

/** Confirma o descarta una marca nueva. Lo hace una PERSONA, nunca el sistema. */
export async function resolverHallazgo(
  hallazgoId: string, estado: 'confirmado' | 'descartado', marcaId: string | null = null,
): Promise<void> {
  const { error } = await supabase.rpc('resolver_hallazgo', {
    p_hallazgo: hallazgoId, p_estado: estado, p_marca_id: marcaId,
  });
  if (error) throw new LevantamientoApiError(error.message, error.code ?? null);
}

// ---------------------------------------------------------------------------
// Enviadores de las colas sin señal. Se registran al importarse este módulo.
// ---------------------------------------------------------------------------
setVisitaUploader({
  uploadVisitaPhoto,
  registrarVisita,
  isPermanentError: esErrorPermanente,
});

setJornadaUploader({
  registrarJornada: registrarJornadaLevantamiento,
  isPermanentError: esErrorPermanente,
});
